import React, { useState, Children } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboardIcon, UsersIcon, UserCheckIcon, ShipIcon, FileTextIcon, CreditCardIcon, AlertTriangleIcon, BarChart3Icon, SmartphoneIcon, GitBranchIcon, ShieldCheckIcon, BuildingIcon, CompassIcon, LeafIcon, ChevronDownIcon, SettingsIcon, AnchorIcon, FileCheckIcon } from 'lucide-react';
interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: NavItem[];
}
interface NavGroup {
  title: string;
  items: NavItem[];
}
export function Sidebar() {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>(['/tour']);
  const toggleExpand = (path: string) => {
    setExpandedItems(prev => prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);
  };
  const navGroups: NavGroup[] = [{
    title: 'OVERVIEW',
    items: [{
      label: 'Dashboard',
      path: '/',
      icon: <LayoutDashboardIcon size={20} data-id="element-253" data-mp="1lym5yvgiosi0" />
    }]
  }, {
    title: 'MANAGEMENT',
    items: [{
      label: 'User Management',
      path: '/users',
      icon: <UsersIcon size={20} data-id="element-254" data-mp="1d00fme4t096o" />
    }, {
      label: 'Role & Permission',
      path: '/roles',
      icon: <ShieldCheckIcon size={20} data-id="element-255" data-mp="1h0u53bxw9w1o" />
    }, {
      label: 'Beneficiary Management',
      path: '/beneficiaries',
      icon: <UserCheckIcon size={20} data-id="element-256" data-mp="29ozpfxl1dj0z" />
    }, {
      label: 'Organization Management',
      path: '/organizations',
      icon: <BuildingIcon size={20} data-id="element-257" data-mp="1vop1b2b42iji" />
    }, {
      label: 'Vessel Management',
      path: '/vessels',
      icon: <ShipIcon size={20} data-id="element-258" data-mp="20qebyguf8u72" />
    }, {
      label: 'BLC Management',
      path: '/blc',
      icon: <AnchorIcon size={20} data-id="element-259" data-mp="p94tnxn1487c" />
    }]
  }, {
    title: 'OPERATIONS',
    items: [{
      label: 'Conservation Management',
      path: '/conservation',
      icon: <LeafIcon size={20} data-id="element-260" data-mp="2lzr3aoury0fr" />
    }, {
      label: 'Harvest Management',
      path: '/harvest',
      icon: <FileTextIcon size={20} data-id="element-261" data-mp="2h7pi2dfmozdl" />
    }, {
      label: 'Tour Management',
      path: '/tour',
      icon: <CompassIcon size={20} data-id="element-262" data-mp="2d6np2ecb1jk2" />,
      children: [{
        label: 'Guide Management',
        path: '/guides',
        icon: <CompassIcon size={18} data-id="element-263" data-mp="238geqecitzr1" />
      }]
    }, {
      label: 'Smart ID & RFID',
      path: '/smart-id',
      icon: <CreditCardIcon size={20} data-id="element-264" data-mp="1ybzdqjk83qwj" />
    }]
  }, {
    title: 'COMPLIANCE',
    items: [{
      label: 'Forest Case & Compliance',
      path: '/compliance',
      icon: <AlertTriangleIcon size={20} data-id="element-265" data-mp="3sltjg4ojppso" />
    }]
  }, {
    title: 'ANALYTICS',
    items: [{
      label: 'Reports & Analytics',
      path: '/reports',
      icon: <BarChart3Icon size={20} data-id="element-266" data-mp="1acgtx8lgiuik" />
    }]
  }, {
    title: 'FIELD',
    items: [{
      label: 'Mobile Field Officer',
      path: '/mobile',
      icon: <SmartphoneIcon size={20} data-id="element-267" data-mp="1k6hac6m6vm6l" />
    }]
  }, {
    title: 'REFERENCE',
    items: [{
      label: 'User Flow',
      path: '/user-flow',
      icon: <GitBranchIcon size={20} data-id="element-268" data-mp="rhjra1a5llcs" />
    }]
  }, {
    title: 'SETTINGS',
    items: [{
      label: 'System Configuration',
      path: '/config',
      icon: <SettingsIcon size={20} data-id="element-269" data-mp="10eu5nvi9pqlx" />
    }]
  }];
  return <div className="w-64 h-screen bg-white border-r border-gray-200 flex flex-col fixed left-0 top-0" data-id="element-270" data-mp="15gjfx8xo208y">
      {/* Logo Area */}
      <div className="p-6 border-b border-gray-200" data-id="element-271" data-mp="2bvan1udduhbf">
        <div className="flex items-center gap-3" data-id="element-272" data-mp="2gu405lorc6b2">
          <div className="w-10 h-10 bg-[#1B5E20] rounded-lg flex items-center justify-center text-white font-bold" data-id="element-273" data-mp="2gssoz0szfw9x">
            PMS
          </div>
          <div data-id="element-274" data-mp="2aewrirtyas2o">
            <div className="font-bold text-sm text-gray-900" data-id="element-275" data-mp="2afexig6f0kns">
              Bangladesh Forest
            </div>
            <div className="text-xs text-gray-600" data-id="element-276" data-mp="2oextybpv67k9">Department</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-4" data-id="element-277" data-mp="2kexh2viey4fb">
        {navGroups.map((group, idx) => <div key={idx} className="mb-6" data-id="element-278" data-mp="2ya99gw93jit2" data-mpi={idx}>
            <div className="px-6 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-279" data-mp="2te11q3x8arvx">
              {group.title}
            </div>
            <div className="space-y-1" data-id="element-280" data-mp="38489nppm0sid">
              {group.items.map(item => {
            const isActive = location.pathname === item.path;
            const hasChildren = item.children && item.children.length > 0;
            const isExpanded = expandedItems.includes(item.path);
            const isChildActive = hasChildren && item.children!.some(c => location.pathname === c.path);
            return <div key={item.path} data-id="element-281" data-mp="4hwotwafum9q">
                    <div className="flex items-center" data-id="element-282" data-mp="4gvaniy4ljns">
                      <Link to={item.path} className={`flex items-center gap-3 px-6 py-2.5 text-sm transition-colors flex-1 ${isActive || isChildActive ? 'bg-[#1B5E20] text-white font-medium' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-283" data-mp="3mo2lzs9gr6rn">
                        {item.icon}
                        <span data-id="element-284" data-mp="3hrkmb6szpym6">{item.label}</span>
                      </Link>
                      {hasChildren && <button onClick={() => toggleExpand(item.path)} className={`pr-4 py-2.5 transition-colors ${isActive || isChildActive ? 'text-white' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-285" data-mp="37zolvp3flwes">
                          <ChevronDownIcon size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-286" data-mp="2z12vj7rpxd3g" />
                        </button>}
                    </div>
                    {hasChildren && isExpanded && <div className="ml-4 border-l border-gray-200" data-id="element-287" data-mp="qsfvvfwwwhzk">
                        {item.children!.map(child => {
                  const isChildItemActive = location.pathname === child.path;
                  return <Link key={child.path} to={child.path} className={`flex items-center gap-3 pl-8 pr-6 py-2 text-sm transition-colors ${isChildItemActive ? 'bg-[#E8F5E9] text-[#1B5E20] font-medium' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-288" data-mp="1egz3ruc1usr4">
                              {child.icon}
                              <span data-id="element-289" data-mp="29qumdi6aqhrr">{child.label}</span>
                            </Link>;
                })}
                      </div>}
                  </div>;
          })}
            </div>
          </div>)}
      </div>

      {/* User Info */}
      <div className="p-6 border-t border-gray-200" data-id="element-290" data-mp="2nir8n5la9xg8">
        <div className="flex items-center gap-3" data-id="element-291" data-mp="2sgsufya3gave">
          <div className="w-10 h-10 bg-[#2E7D32] rounded-full flex items-center justify-center text-white font-semibold" data-id="element-292" data-mp="2sg0ygmcl4b4l">
            A
          </div>
          <div data-id="element-293" data-mp="32f06c2pj02qj">
            <div className="text-sm font-medium text-gray-900" data-id="element-294" data-mp="32egr4tr9fsg7">Admin</div>
            <div className="text-xs text-gray-600" data-id="element-295" data-mp="36glghgaoe2x1">HQ Level</div>
          </div>
        </div>
      </div>
    </div>;
}